import React, { useCallback, useEffect, useState } from "react";
import { Checkbox } from "@mui/material";
import OpTaskTrackOtherExpense from "./OpTaskTrackOtherExpense";
import { useSnackbar } from "notistack";
import { useDispatch } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import LoadingSpinner from "../../../../../components/common/loader/LoadingSpinner";
import { fetchDescriptionTasksProjectTracking } from "../../../../../store/slices/DescriptionTask";
import { fetchWorkEntriesProjectTracking } from "../../../../../store/slices/workentriesSlice";
import DateConversion from "../../../../../utils/DateConversion";
import {
  markComplete,
  markNonComplete,
  updateTaskHours,
} from "../../../../../store/slices/ProjectOngoing/projectOngoingSlice";
import ProgressTracker from "../../../../../utils/ProgressTracker";
import useToolGuideSync from "../../../../../features/toolGuide/useToolGuideSync";
import { MANAGER_BUCKET_KEYS } from "../../../../../features/toolGuide/toolGuideConfig";

const OpTaskTrackManager = ({
  row,
  matchedOpWorkEntries,
  matchedHardwareDescriptionsPartsOP,
  selectedEmployee,
  showListState,
  setIsLoading,
  isViewOnly = false,
}) => {
  const { formatDateByCountry, getDatePlaceholder, getDateFormat } =
    DateConversion();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { syncToolGuideStep } = useToolGuideSync(
    MANAGER_BUCKET_KEYS.PROJECT_ONGOING,
    "L4_PM"
  );
  const [data, setData] = useState(matchedOpWorkEntries || []);
  const [loadingRows, setLoadingRows] = useState({});

  useEffect(() => {
    setData(matchedOpWorkEntries);
  }, [matchedOpWorkEntries]);

  const [editedTask, setEditedTask] = useState({});
  const [editedNotes, setEditedNotes] = useState({});
  const formatTwoDecimals = (value) =>
    (Math.trunc((Number(value) || 0) * 100) / 100).toFixed(2);

  const handleUpdateHours = async (taskId) => {
    if (isViewOnly) return;
    syncToolGuideStep("view-details");
    if (!taskId) return;
    const value = editedTask[taskId];
    const entry = data?.find((e) => e._id === taskId);
    const hoursToSave =
      value !== undefined && value !== null && value !== ""
        ? Number(value)
        : Number(entry?.hoursspent) ?? 0;
    if (Number.isNaN(hoursToSave) || hoursToSave < 0) return;
    const projectid = row?._id;
    const updatedData = { hoursspent: hoursToSave };
    try {
      await dispatch(
        updateTaskHours({
          projectid,
          taskownerid: taskId,
          updatedData,
        })
      ).unwrap();
      setEditedTask((prev) => {
        const next = { ...prev };
        delete next[taskId];
        return next;
      });
    } catch (err) {
      enqueueSnackbar(err?.message || err || "Failed to save hours", {
        variant: "error",
      });
    } finally {
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
    }
  };

  const [updatedDateWorkEntries, setUpdatedDateWorkEntries] = useState({});

  const handleWorkEntriesDateSubmit = useCallback(
    async (entry) => {
      syncToolGuideStep("view-details");
      const matchedEntry = updatedDateWorkEntries[entry._id] || {};
      const matchedStartDate = matchedEntry.startdate || null;
      const matchedEndDate = matchedEntry.enddate || null;

      const updatedData = {
        startdate: matchedStartDate ?? entry.startdate ?? null,
        enddate: matchedEndDate ?? entry.enddate ?? null,
      };
      try {
        await dispatch(
          updateTaskHours({
            projectid: row?._id,
            taskownerid: entry?._id,
            updatedData,
          })
        );
        dispatch(fetchWorkEntriesProjectTracking());
        dispatch(fetchDescriptionTasksProjectTracking());
      } catch {
        enqueueSnackbar("Failed To Add Date", { variant: "error" });
      } finally {
        setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
      }
    },
    [updatedDateWorkEntries, row, dispatch, enqueueSnackbar, syncToolGuideStep]
  );

  const workEntriesDateChange = useCallback(
    (date, field, entry) => {
      if (isViewOnly) return;
      setUpdatedDateWorkEntries((prev) => ({
        ...prev,
        [entry._id]: {
          ...prev[entry._id],
          [field]: date,
        },
      }));
      if (field === "enddate") {
        const newentry = {
          ...entry,
          enddate: date,
        };
        handleWorkEntriesDateSubmit(newentry);
      }
    },
    [handleWorkEntriesDateSubmit, isViewOnly]
  );

  const handleUpdateNotes = async (taskId) => {
    if (isViewOnly) return;
    syncToolGuideStep("view-details");
    const value = editedNotes[taskId]; // get the value for this specific task
    if (!taskId) return;
    const projectid = row?._id;
    const updatedData = {
      notes: value,
    };
    try {
      await dispatch(
        updateTaskHours({
          projectid,
          taskownerid: taskId,
          updatedData,
        })
      ).unwrap();
    } catch (err) {
      enqueueSnackbar(err, { variant: "error" });
    } finally {
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
    }
  };

  const handleTaskMarkComplete = async (entry) => {
    if (isViewOnly) return;
    syncToolGuideStep("view-details");
    const projectid = row?._id;
    const taskid = entry?._id;
    setLoadingRows((prev) => ({ ...prev, [entry?._id]: true }));
    setIsLoading(true);
    try {
      await dispatch(markComplete({ projectid, taskid }));
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
      enqueueSnackbar("Task Completed Successfully", {
        variant: "success",
      });
    } catch {
      enqueueSnackbar("Failed To Complete Task", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
      setIsLoading(false);
    }
  };

  const handleMarkNonComplete = async (entry) => {
    if (isViewOnly) return;
    const projectid = row?._id;
    const taskid = entry?._id;
    setLoadingRows((prev) => ({ ...prev, [entry?._id]: true }));
    setIsLoading(true);
    try {
      await dispatch(markNonComplete({ projectid, taskid }));
      setEditedTask((prev) => {
        const next = { ...prev };
        delete next[entry?._id];
        return next;
      });
      setUpdatedDateWorkEntries((prev) => {
        const next = { ...prev };
        delete next[entry?._id];
        return next;
      });
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
      enqueueSnackbar("Task Not-Completed Successfully", {
        variant: "success",
      });
    } catch {
      enqueueSnackbar("Failed To Non-Complete Task", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
      setIsLoading(false);
    }
  };

  return (
    <div>

      {showListState === "tasktable" && (
        <div className="w-full max-h-[24rem] overflow-y-auto overflow-x-hidden">
          {/* Header */}
          <div className="z-[50] sticky top-0 flex bg-[#e8e8e8] text-black text-[10px] font-semibold break-all whitespace-normal">
            <div className="p-1 w-[10%] text-center">Tasks</div>
            <div className=" w-[10%] flex justify-center items-center align-middle text-center break-words whitespace-normal">
              Hours Assigned
            </div>{" "}
            <div className="p-1 w-[10%] text-center">Hours Spent</div>{" "}
            <div className="p-1 w-[13%] text-center">Start Date</div>
            <div className="p-1 w-[13%]">End Date</div>
            <div className="p-1 w-[10%]">Notes</div>
            <div className="p-1 w-[24%] text-center">Tracker</div>
            <div className="p-1 w-[10%] text-center">Action</div>
          </div>

          <div className="flex text-[10px] break-all whitespace-normal max-h-14">
            <div className="p-1 text-center border-b border-[#dee2e6] w-[10%] flex justify-center align-middle items-center">
              {"Total"}
            </div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%] flex justify-center align-middle items-center">
              {data?.reduce(
                (sum, item) => sum + (item?.hoursassigned || 0),
                0
              )}
            </div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center flex justify-center align-middle items-center">
              {formatTwoDecimals(
                data?.reduce((sum, item) => sum + (item?.hoursspent || 0), 0)
              )}
            </div>
            <div className="p-1 border-b border-[#dee2e6] w-[13%] text-center"></div>
            <div className="p-1 border-b border-[#dee2e6] w-[13%] text-center"></div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center"></div>
            <div className="p-2.5 border-b border-[#dee2e6] w-[24%] text-center">
              {data?.every((entry) => entry.isTaskComplete) ? (
                <>
                  {(() => {
                    const hoursAssigned = data?.reduce(
                      (sum, item) => sum + (item?.hoursassigned || 0),
                      0
                    );
                    const hoursSpent = data?.reduce(
                      (sum, item) => sum + (item?.hoursspent || 0),
                      0
                    );

                    const trackerPercentage =
                      hoursAssigned > 0
                        ? (hoursSpent / hoursAssigned) * 100
                        : 0;

                    const displayValue =
                      trackerPercentage < 100 ? 100 : trackerPercentage;

                    return <ProgressTracker value={displayValue} />;
                  })()}{" "}
                </>
              ) : (
                <>
                  {(() => {
                    const hoursAssigned = data?.reduce(
                      (sum, item) => sum + (item?.hoursassigned || 0),
                      0
                    );
                    const hoursSpent = data?.reduce(
                      (sum, item) => sum + (item?.hoursspent || 0),
                      0
                    );
                    const trackerPercentage =
                      hoursAssigned > 0
                        ? (hoursSpent / hoursAssigned) * 100
                        : 0;
                    return <ProgressTracker value={trackerPercentage} />;
                  })()}
                </>
              )}
            </div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center"></div>
          </div>

          {/* Rows */}
          {data?.length > 0 ? (
            data?.map((entry, index) => (
              <div
                key={entry}
                className="flex text-[10px] break-all whitespace-normal max-h-14"
              >
                <div className="p-1 border-b border-[#dee2e6] w-[10%] flex justify-center align-middle items-center">
                  {entry.task}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] flex justify-center align-middle items-center">
                  {entry.hoursassigned || ""}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center flex justify-center align-middle items-center">
                  {/* {entry.hoursspent || ""} */}
                  {entry.isTaskComplete ? (
                    <span>{entry.hoursspent || ""}</span>
                  ) : (
                    <input
                      type="text"
                      value={
                        editedTask[entry._id] ?? entry.hoursspent ?? ""
                      }
                      placeholder="enter hours"
                      disabled={isViewOnly}
                      className={`border border-gray-200 w-full max-w-full p-1 rounded-md text-center ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : ""}`}
                      onChange={(e) => {
                        if (isViewOnly) return;
                        const value = e.target.value;
                        const parsedValue = Number(value);

                        if (
                          value !== "" &&
                          parsedValue > Number(entry.hoursassigned || 0)
                        ) {
                          enqueueSnackbar(
                            "Hours Spent is exceeding the Hours Assigned",
                            {
                              variant: "error",
                            }
                          );
                        }
                        // Allow decimal hours up to 2 digits after dot
                        if (!/^\d*\.?\d{0,2}$/.test(value)) return;
                        if (value !== "" && parsedValue < 0) return;

                        setEditedTask((prev) => ({
                          ...prev,
                          [entry._id]: value,
                        }));
                      }}
                      onBlur={() => handleUpdateHours(entry._id)}
                    />
                  )}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[13%] text-center flex justify-center align-middle items-center">
                  {/* {entry.startdate
                        ? formatDateByCountry(entry.startdate)
                        : ""}{" "} */}
                  {entry.isTaskComplete ? (
                    <div className="text-black">
                      {entry.startdate
                        ? formatDateByCountry(entry.startdate)
                        : ""}
                    </div>
                  ) : (
                    <DatePicker
                      dateFormat={getDateFormat()}
                      disabled={isViewOnly}
                      className={`w-full h-full text-center outline-none border p-1 border-gray-300 rounded-md bg-white ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : ""}`}
                      placeholderText={getDatePlaceholder()}
                      popperClassName="z-[1500]"
                      minDate={
                        entry.startdate
                          ? new Date(entry.startdate)
                          : row?.DateRequested
                            ? new Date(row.DateRequested)
                            : null
                      }
                      maxDate={new Date()}
                      selected={
                        updatedDateWorkEntries[entry._id]?.startdate
                          ? updatedDateWorkEntries[entry._id].startdate
                          : entry.startdate
                            ? new Date(entry.startdate)
                            : null
                      }
                      onChange={(date) =>
                        workEntriesDateChange(date, "startdate", entry)
                      }
                    />
                  )}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[13%] text-center flex justify-center align-middle items-center">
                  {/* {entry.enddate ? formatDateByCountry(entry.enddate) : ""} */}
                  {entry.isTaskComplete ? (
                    <div className="text-black">
                      {entry.enddate
                        ? formatDateByCountry(entry.enddate)
                        : ""}
                    </div>
                  ) : (
                    <DatePicker
                      dateFormat={getDateFormat()}
                      disabled={isViewOnly}
                      className={`w-full h-full text-center outline-none border p-1 border-gray-300 rounded-md bg-white ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : ""}`}
                      placeholderText={getDatePlaceholder()}
                      popperClassName="z-[1500]"
                      minDate={
                        // ✅ Use updated startdate if available, else fallback to entry.startdate
                        updatedDateWorkEntries[entry._id]?.startdate
                          ? new Date(
                            updatedDateWorkEntries[entry._id].startdate
                          )
                          : entry.startdate
                            ? new Date(entry.startdate)
                            : null
                      }
                      selected={
                        updatedDateWorkEntries[entry._id]?.enddate
                          ? updatedDateWorkEntries[entry._id].enddate
                          : entry.enddate
                            ? new Date(entry.enddate)
                            : null
                      }
                      onChange={(date) =>
                        workEntriesDateChange(date, "enddate", entry)
                      }
                    />
                  )}
                </div>
                <div
                  title={`${entry.notes}`}
                  className="p-1 flex justify-center align-middle items-center border-b border-[#dee2e6] w-[10%] text-center break-words whitespace-normal max-h-14 overflow-auto"
                >
                  {/* {entry.notes || ""} */}
                  {entry.isTaskComplete ? (
                    <span>{entry.notes || ""}</span>
                  ) : (
                    <input
                      type="text"
                      value={editedNotes[entry._id] ?? entry.notes ?? ""}
                      placeholder="enter note"
                      disabled={isViewOnly}
                      className={`border border-gray-200 w-full max-w-full p-1 rounded-md text-center ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : ""}`}
                      onChange={(e) => {
                        if (isViewOnly) return;
                        const value = e.target.value;
                        setEditedNotes((prev) => ({
                          ...prev,
                          [entry._id]: value,
                        }));
                      }}
                      onBlur={() => handleUpdateNotes(entry._id)}
                    />
                  )}
                </div>
                <div className="p-2.5 border-b border-[#dee2e6] w-[24%] text-center">
                  {/* <ProgressTracker
                        value={(() => {
                          const rawValue =
                            ((Number(entry.hoursspent) || 0) /
                              (entry.hoursassigned || 1)) *
                            100;
                          return rawValue > 100 ? 100 : rawValue; // Cap at 100%, not floor at 100%
                        })()}
                      /> */}
                  {entry.isTaskComplete ? (
                    <ProgressTracker
                      value={(() => {
                        const rawValue =
                          ((Number(entry.hoursspent) || 0) /
                            entry.hoursassigned) *
                          100;
                        return rawValue < 100 ? 100 : rawValue;
                      })()}
                    />
                  ) : (
                    <ProgressTracker
                      value={
                        ((Number(
                          editedTask[entry._id] ?? entry.hoursspent
                        ) || 0) /
                          entry.hoursassigned) *
                        100
                      }
                    />
                  )}
                </div>
                <div
                  className={` p-1 border-b border-[#dee2e6] w-[10%] text-center cursor-default ${row.Assignedperson.every(
                    (person) => person.isProjectComplete === true
                  )
                      ? "cursor-default"
                      : ""
                    }`}
                  title={`${entry.isTaskComplete ? "Completed" : "Pending"
                    }`}
                >
                  {/* <Checkbox
                        disabled={row.Assignedperson?.every(
                          (person) => person.isProjectComplete === true
                        )}
                        className="cursor-default pointer-events-none"
                        readOnly
                        checked={entry.isTaskComplete}
                      /> */}
                  {loadingRows[index] ? (
                    <LoadingSpinner />
                  ) : (
                    <Checkbox
                      checked={entry.isTaskComplete}
                      disabled={
                        isViewOnly ||
                        row.Assignedperson?.every(
                          (person) => person.isProjectComplete === true
                        ) ||
                        ((Number(
                          editedTask[entry._id] ?? entry.hoursspent
                        ) || 0) /
                          entry.hoursassigned) *
                        100 <
                        1
                      }
                      onChange={(e) => {
                        if (isViewOnly) return;
                        const isChecked = e.target.checked;
                        const currentHours =
                          editedTask[entry._id] !== undefined
                            ? Number(editedTask[entry._id])
                            : Number(entry.hoursspent);

                        const startDate =
                          updatedDateWorkEntries[entry._id]?.startdate ||
                          entry.startdate;
                        const endDate =
                          updatedDateWorkEntries[entry._id]?.enddate ||
                          entry.enddate;

                        if (isChecked) {
                          if (!currentHours || currentHours <= 0) {
                            enqueueSnackbar("Please Enter Hours Spent", {
                              variant: "error",
                            });
                            e.preventDefault();
                            return;
                          }
                          if (!startDate || !endDate) {
                            enqueueSnackbar("Please Select End Date", {
                              variant: "error",
                            });
                            return;
                          }
                          const doComplete = () => {
                            handleTaskMarkComplete(entry, index);
                            handleWorkEntriesDateSubmit(entry);
                          };
                          if (editedTask[entry._id] !== undefined) {
                            handleUpdateHours(entry._id).then(doComplete).catch(() => {});
                          } else {
                            doComplete();
                          }
                        } else {
                          handleMarkNonComplete(entry);
                        }
                      }}
                    />
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="flex text-[14px] break-all whitespace-normal max-h-14 p-1">
              <p className="text-red-500 text-center">
                {" "}
                No Tracking Task Entries Found
              </p>
            </div>
          )}
        </div>
      )}

      {showListState === "otherexpenses" && (
        <OpTaskTrackOtherExpense
          row={row}
          setIsLoading={setIsLoading}
          data={data}
          matchedHardwareDescriptionsPartsOP={
            matchedHardwareDescriptionsPartsOP
          }
          selectedEmployee={selectedEmployee}
          isViewOnly={isViewOnly}
        />
      )}
    </div>
  );
};

export default OpTaskTrackManager;
